import mapboxgl from 'mapbox-gl';
import { Map } from 'react-map-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import Markers from './Markers';
import Navigation from './Navigation';
import PhotoModal from './PhotoModal';
import PhotoDraftPanel from './PhotoDraftPanel';
import { findPhoto, findPlace } from './travelData';

mapboxgl.workerUrl = `${process.env.PUBLIC_URL}/mapbox-gl-csp-worker.js`;

const fallbackMapboxApiKey =
  'pk.eyJ1Ijoid2VzbHlsb2Rld2lqa3MiLCJhIjoiY2xlZWdxY201MDFpajN2b29lMnRkZmowZSJ9.__uIHphVxAvqUjLrWPSvXA';
const mapboxApiKey = process.env.REACT_APP_MAPBOX_API_KEY || fallbackMapboxApiKey;

const overview = {
  longitude: 14,
  latitude: 43.5,
  zoom: 1.8,
};

function MapView({ navigate, search, pathname }) {
  const mapRef = useRef(null);
  const [activePlaceId, setActivePlaceId] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showBackButton, setShowBackButton] = useState(false);
  const [draftCoordinate, setDraftCoordinate] = useState(null);
  const queryPhotoId = useMemo(() => new URLSearchParams(search).get('loc'), [search]);

  useEffect(() => {
    if (!queryPhotoId) return;

    const photo = findPhoto(queryPhotoId);
    if (!photo) return;

    setActivePlaceId(photo.placeId);
    setSelectedPhoto(null);
    setShowBackButton(true);

    window.setTimeout(() => {
      mapRef.current?.flyTo({
        center: [photo.longitude, photo.latitude],
        zoom: Math.max(photo.placeZoom || 10, 12),
        speed: 1.1,
      });
    }, 250);
  }, [queryPhotoId]);

  const focusPlace = (place) => {
    setActivePlaceId(place.id);
    setSelectedPhoto(null);
    setShowBackButton(true);
    window.history.replaceState({}, '', `/map?place=${place.id}`);
    mapRef.current?.flyTo({
      center: [place.longitude, place.latitude],
      zoom: place.zoom,
      speed: 1.1,
    });
  };

  const resetOverview = () => {
    setActivePlaceId(null);
    setSelectedPhoto(null);
    setShowBackButton(false);
    window.history.replaceState({}, '', '/map');
    mapRef.current?.flyTo({
      center: [overview.longitude, overview.latitude],
      zoom: overview.zoom,
      speed: 1,
    });
  };

  const openPhoto = (photo) => {
    const place = findPlace(photo.placeId);
    setSelectedPhoto(photo);
    if (place) setActivePlaceId(place.id);
    window.history.replaceState({}, '', `/map?loc=${photo.id}`);
  };

  const handleMapClick = (event) => {
    if (!event.lngLat) return;
    setDraftCoordinate({
      longitude: Number(event.lngLat.lng.toFixed(6)),
      latitude: Number(event.lngLat.lat.toFixed(6)),
    });
  };

  return (
    <main className="map-page">
      <Navigation navigate={navigate} pathname={pathname} />
      <Map
        id="map"
        ref={mapRef}
        mapboxAccessToken={mapboxApiKey}
        mapStyle="mapbox://styles/weslylodewijks/cleehorys000m01oa0jw5jhx9"
        projection="globe"
        initialViewState={overview}
        onClick={handleMapClick}
      >
        <Markers
          activePlaceId={activePlaceId}
          focusPlace={focusPlace}
          openPhoto={openPhoto}
        />
      </Map>

      {showBackButton && (
        <button className="map-back-button" type="button" onClick={resetOverview}>
          Ga terug
        </button>
      )}

      <PhotoDraftPanel coordinate={draftCoordinate} activePlaceId={activePlaceId} />
      <PhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
    </main>
  );
}

export default MapView;
